import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { supabase } from "@/integrations/supabase/client";

/**
 * On-domain consultation request. Collects situation only — no product picker,
 * no budget field, no coverage-amount field.
 */
const schema = z.object({
  full_name: z.string().trim().min(1, "Full name is required").max(100),
  email: z.string().trim().email("Enter a valid email").max(255),
  phone: z.string().trim().min(7, "Enter a phone number").max(30),
  situation: z
    .string()
    .trim()
    .min(20, "Please share at least a few sentences (20+ characters).")
    .max(2000),
  preferred_time: z.string().trim().max(120).optional(),
});

type Values = z.infer<typeof schema>;

const inputCls =
  "w-full border border-bronze-600/40 bg-white px-4 py-3 font-sans text-[15px] text-charcoal-900 outline-none focus:border-navy-800";

export function ConsultationRequestForm() {
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  async function onSubmit(values: Values) {
    setSubmitting(true);
    const { error } = await supabase.from("leads").insert({
      full_name: values.full_name,
      email: values.email.trim().toLowerCase(),
      phone: values.phone,
      source: "consultation_request",
      notes: [
        `Household situation: ${values.situation}`,
        values.preferred_time ? `Preferred time: ${values.preferred_time}` : null,
      ]
        .filter(Boolean)
        .join("\n"),
    });
    setSubmitting(false);
    if (error) {
      toast.error("We couldn't send that. Please try again.");
      return;
    }
    reset();
    setSent(true);
  }

  if (sent) {
    return (
      <div className="bg-white p-8 text-center" style={{ boxShadow: "0 10px 40px -10px rgba(168,139,92,0.18)" }}>
        <Eyebrow color="bronze" align="center">Received</Eyebrow>
        <p className="mt-5 font-sans text-[16px] leading-relaxed text-charcoal-900">
          We'll reach out to schedule a 30-minute conversation about your family's
          situation.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-white p-8"
      style={{ boxShadow: "0 10px 40px -10px rgba(168,139,92,0.18)" }}
    >
      <Eyebrow color="bronze">Request a Consultation</Eyebrow>
      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field label="Full name" error={errors.full_name?.message}>
          <input className={inputCls} autoComplete="name" {...register("full_name")} />
        </Field>
        <Field label="Email" error={errors.email?.message}>
          <input
            className={inputCls}
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="email"
            {...register("email")}
          />
        </Field>
        <Field label="Phone" error={errors.phone?.message}>
          <input className={inputCls} type="tel" autoComplete="tel" {...register("phone")} />
        </Field>
        <Field label="Preferred time (optional)" error={errors.preferred_time?.message}>
          <input className={inputCls} placeholder="Weekday evenings" {...register("preferred_time")} />
        </Field>
      </div>
      <div className="mt-5">
        <Field label="Your household situation" error={errors.situation?.message}>
          <textarea rows={5} className={inputCls} {...register("situation")} />
        </Field>
      </div>
      <div className="mt-6">
        <PrimaryButton type="submit" size="lg" disabled={submitting} className="w-full">
          {submitting ? "Sending…" : "Request a Consultation"}
        </PrimaryButton>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500">
        {label}
      </span>
      <div className="mt-2">{children}</div>
      {error && <span className="mt-1 block font-sans text-[12px] text-danger">{error}</span>}
    </label>
  );
}
